/**
 * /app/alarms — Tier 1 alarm management.
 *
 * Two tabs:
 *   - Alarms: active + recently-cleared alarm list with ack action
 *   - Rules: rule editor (Phase 0 placeholder until B0.7 polish)
 *
 * Live mode reads alarm events + rules from `/v1/iot/alarms/*` (via the
 * AlarmsProvider) and the rule editor targets real org tags; stub mode keeps
 * the FX_ALARMS / FX_CATALOG_TAGS fixtures for local UI dev.
 *
 * Each alarm row offers "Ask" (2026-09-28; lib/copilot/ask) where the AI Casting Consultant's page opens: a question
 * about why it fired, with the alarm as the context.
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { buildCopilotHref } from "@/lib/copilot/context";
import {
  AlertTriangle,
  CheckCircle2,
  Edit3,
  Filter,
  PauseCircle,
  Plus,
  ShieldAlert,
  Sliders,
  Trash2,
} from "lucide-react";
import { PageContainer } from "@/routes/_layout";
import { PageHeader } from "@/lib/ui/page-header";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/lib/ui/card";
import { Input } from "@/lib/ui/input";
import { Sheet, SheetHeader } from "@/lib/ui/sheet";
import { EmptyState } from "@/lib/ui/empty-state";
import { cn } from "@/lib/ui/cn";
import { AlarmList, type AlarmItem } from "@/lib/widgets";
import { useAlarms, type AlarmRule } from "@/lib/alarms/store";
import { useAlarmWhereResolver } from "@/lib/alarms/where";
import { OrgUnits } from "@/components/units/OrgUnits";
import { useShownPrefs } from "@/lib/units/context";
import { shownFor } from "@/lib/units/display";
import type { DisplayPrefs } from "@/lib/units/prefs";
import { useTagUnitOf } from "@/lib/alarms/tag-units";
import {
  thresholdConditionText,
  thresholdFormText,
  thresholdRetargeted,
  thresholdLabel,
  thresholdSaved,
  type OpenedThreshold,
} from "@/lib/alarms/threshold";
import { useOrg } from "@/lib/org-context";
import { useToast } from "@/lib/ui/toast";
import { FX_CATALOG_TAGS } from "@/lib/compute/__fixtures";
import { isLiveApi, listOrgTags, type TagOption } from "@/lib/api";
import { TagSearchCombobox } from "@/components/twin/TagSearchCombobox";
import { AlarmEventDetailSheet } from "@/components/iot/AlarmEventDetailSheet";
import { SpcAlarmConditionFields } from "@/components/iot/SpcAlarmConditionFields";
import {
  SPC_ALARM_RULES,
  buildSpcAlarmCondition,
  spcAlarmConditionText,
  spcAlarmDraftValid,
  spcAlarmKeepOffered,
} from "@/lib/alarms/spc-condition";
import { useSpcAlarmOfferedRules } from "@/lib/alarms/spc-offered";
import { usePageCopilotContext } from "@/lib/copilot/page-context";
import { consultantLauncher } from "@/lib/copilot/availability";
import { aboutFor, itemLabel } from "@/lib/copilot/ask";
import { useCopilotAvailability } from "@/lib/api/copilot-imports";
import { AskCopilot } from "@/components/copilot/AskCopilot";
import { StationAlertsSection } from "@/components/twin/alerts/StationAlertsSection";
import { noAlarmsWords, useAlarmFeedState } from "@/lib/api/live-feed";

type Tab = "alarms" | "rules";
type AlarmFilter = "active-unacked" | "active" | "acknowledged" | "cleared" | "all";

const FILTER_DEFS: Array<{ key: AlarmFilter; label: string }> = [
  { key: "active-unacked", label: "Needs attention" },
  { key: "active", label: "Active" },
  { key: "acknowledged", label: "Acknowledged" },
  { key: "cleared", label: "Cleared" },
  { key: "all", label: "All" },
];

/** The alarms page: the org's display units under it, so a rule's threshold is read and typed in them. */
export function Alarms() {
  return (
    <OrgUnits>
      <AlarmsContent />
    </OrgUnits>
  );
}

function AlarmsContent() {
  const navigate = useNavigate();
  const unitOf = useTagUnitOf();
  const [searchParams] = useSearchParams();
  const { events: alarms, ackEvent } = useAlarms();
  // A correlation "create alarm" hand-off arrives as ?tab=rules&ar_* — pre-open
  // the Rules tab and pass the suggested rule down to the editor.
  const alarmPrefill = useMemo<Partial<DraftRule> | null>(() => {
    const name = searchParams.get("ar_name");
    const op = searchParams.get("ar_op");
    const value = searchParams.get("ar_value");
    const label = searchParams.get("ar_label");
    const tagId = searchParams.get("ar_tag_id");
    const tagName = searchParams.get("ar_tag_name");
    const p: Partial<DraftRule> = {};
    if (name) p.name = name;
    if (op === "gt" || op === "lt") p.op = op;
    if (value) p.value = value;
    if (label) p.targetLabel = label;
    // Correlation hand-off can carry the driving parameter's bound tag → the
    // rule opens with the real target tag pre-selected (tag name overrides the
    // param label so the editor shows what it'll actually watch).
    if (tagId) {
      p.targetTagId = tagId;
      if (tagName) p.targetLabel = tagName;
    }
    return Object.keys(p).length > 0 ? p : null;
  }, [searchParams]);
  const [tab, setTab] = useState<Tab>(
    searchParams.get("tab") === "rules" || searchParams.get("ar_name") ? "rules" : "alarms",
  );
  const [filter, setFilter] = useState<AlarmFilter>("active-unacked");
  const [selectedEvent, setSelectedEvent] = useState<AlarmItem | null>(null);
  // A2 — resolve WHERE each alarm fired (bound entity + stage), with a
  // client-side fallback over the org's bindings when the backend message
  // didn't carry it.
  const resolveWhere = useAlarmWhereResolver();
  // Where the AI Casting Consultant's page opens, each alarm asks it with its context (AskCopilot), in place of the
  // Phase-0 "Investigate" hand-off, which that page does not read.
  const consultantOpens = consultantLauncher(useCopilotAvailability()).opensPage;

  usePageCopilotContext({ route: "/app/alarms", label: "Alarms", about: aboutFor({ page: "alarms", label: "Alarms" }) });
  // Where nothing can raise an alarm (gateways that sent nothing in 30 days, or data from files alone), an empty list
  // says why instead of "All clear" (lib/api/live-feed; the 2026-10-24 page sweep, H1, and the #1641 review, I1). A
  // live gateway or hand entry, and an unknown state, keep the list's own words.
  const noAlarms = noAlarmsWords(useAlarmFeedState());

  const filtered = useMemo(() => {
    switch (filter) {
      case "active-unacked":
        return alarms.filter((a) => a.active && !a.acknowledged);
      case "active":
        return alarms.filter((a) => a.active);
      case "acknowledged":
        return alarms.filter((a) => a.acknowledged);
      case "cleared":
        return alarms.filter((a) => !a.active);
      case "all":
      default:
        return alarms;
    }
  }, [alarms, filter]);

  const counts = useMemo(
    () => ({
      "active-unacked": alarms.filter((a) => a.active && !a.acknowledged).length,
      active: alarms.filter((a) => a.active).length,
      acknowledged: alarms.filter((a) => a.acknowledged).length,
      cleared: alarms.filter((a) => !a.active).length,
      all: alarms.length,
    }),
    [alarms],
  );

  const acknowledge = (a: AlarmItem) => {
    ackEvent(a.id);
  };

  const stats = useMemo(() => {
    const active = alarms.filter((a) => a.active);
    return {
      activeCount: active.length,
      criticalCount: active.filter((a) => a.severity === "danger").length,
      unackedCount: active.filter((a) => !a.acknowledged).length,
      clearedToday: alarms.filter((a) => !a.active).length,
    };
  }, [alarms]);

  return (
    <PageContainer className="max-w-7xl">
      <PageHeader
        breadcrumb={
          <>
            <Link to="/app" className="hover:text-on-surface">
              App
            </Link>
            <span aria-hidden>/</span>
            <span className="text-on-surface">Alarms</span>
          </>
        }
        title="Alarms"
        description="The alarms that are active, or cleared lately. Acknowledge one to say it is being handled, or open it to see the tag or heat behind it."
        actions={
          <Button size="sm" variant="outline" onClick={() => setTab("rules")}>
            <Sliders className="mr-1.5 size-4" />
            Manage rules
          </Button>
        }
      />

      {/* The stations' alerts first (station workspaces, step 7): raised from the levels, Mine on for a member. */}
      <StationAlertsSection />

      {/* Top stat row */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard
          icon={ShieldAlert}
          label="Active alarms"
          value={stats.activeCount}
          tone={stats.activeCount > 0 ? "warning" : "default"}
        />
        <StatCard
          icon={AlertTriangle}
          label="Critical"
          value={stats.criticalCount}
          tone={stats.criticalCount > 0 ? "danger" : "default"}
        />
        <StatCard
          icon={PauseCircle}
          label="Unacknowledged"
          value={stats.unackedCount}
          tone={stats.unackedCount > 0 ? "warning" : "default"}
        />
        <StatCard
          icon={CheckCircle2}
          label="Cleared today"
          value={stats.clearedToday}
          tone="success"
        />
      </div>

      {/* Tab strip */}
      <div className="flex items-end gap-4 border-b border-border">
        <TabTrigger active={tab === "alarms"} onClick={() => setTab("alarms")}>
          Alarms
        </TabTrigger>
        <TabTrigger active={tab === "rules"} onClick={() => setTab("rules")}>
          <span data-tour="alarm-rules-tab">Rules</span>
        </TabTrigger>
        {/* Who gets which alarm email is set with the rest of the org's email, in Settings (the Navigation review:
            the alarms' recipients move into Settings, Email). */}
        <Link
          to="/app/settings?tab=email"
          className="ml-auto pb-2 text-xs text-on-surface-muted underline-offset-4 hover:text-on-surface hover:underline"
        >
          Who gets alarm email: Settings, Email
        </Link>
      </div>

      {tab === "alarms" ? (
        <div className="flex flex-col gap-4">
          {/* Filter chips */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5">
              <Filter className="size-4 text-on-surface-muted" />
              <span className="text-xs font-medium uppercase tracking-wide text-on-surface-muted">
                Filter
              </span>
            </div>
            <div className="flex flex-wrap gap-1">
              {FILTER_DEFS.map((def) => (
                <button
                  key={def.key}
                  type="button"
                  onClick={() => setFilter(def.key)}
                  className={cn(
                    "rounded-full px-3 py-1 text-xs font-medium transition-colors",
                    filter === def.key
                      ? "bg-brand text-brand-foreground"
                      : "bg-surface-muted text-on-surface-muted hover:bg-surface-muted/70 hover:text-on-surface",
                  )}
                >
                  {def.label}
                  <span
                    className={cn(
                      "ml-1.5 rounded-full px-1.5 py-0.5 font-mono text-[10px]",
                      filter === def.key
                        ? "bg-brand-foreground/20"
                        : "bg-surface/60",
                    )}
                  >
                    {counts[def.key]}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {noAlarms && filtered.length === 0 ? (
            <EmptyState icon={ShieldAlert} title={noAlarms.title} description={noAlarms.description} />
          ) : (
            <AlarmList
              alarms={filtered}
              onAcknowledge={acknowledge}
              onView={(a) => setSelectedEvent(a)}
              resolveWhere={(a) => resolveWhere(a).label}
              onInvestigate={
                consultantOpens
                  ? undefined
                  : (alarm) =>
                      navigate(
                        buildCopilotHref({
                          kind: "alarm",
                          ref: alarm.id,
                          where: resolveWhere(alarm).label,
                        }),
                      )
              }
              renderAsk={(alarm) => <AskCopilot {...alarmAsk(alarm, resolveWhere(alarm).label)} />}
            />
          )}
        </div>
      ) : (
        <RulesTab prefill={alarmPrefill} />
      )}

      <AlarmEventDetailSheet
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
        unitOf={unitOf}
      />
    </PageContainer>
  );
}

/**
 * An alarm's ask (2026-09-28): why it fired and what to check first, about the alarm by its title and where it fired.
 * An alarm names a tag, not a brief's condition, so the context carries no condition key.
 */
function alarmAsk(alarm: AlarmItem, where: string | undefined) {
  return {
    question: `Why did "${alarm.title}" fire${where ? ` at ${where}` : ""}, and what should we check first?`,
    about: aboutFor({ page: "alarms", label: itemLabel("Alarm", where ? `${alarm.title}, ${where}` : alarm.title) }),
  };
}

function TabTrigger({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "-mb-px border-b-2 px-1 pb-2.5 text-sm font-medium transition-colors",
        active
          ? "border-brand text-on-surface"
          : "border-transparent text-on-surface-muted hover:border-border hover:text-on-surface",
      )}
    >
      {children}
    </button>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
  tone = "default",
}: {
  label: string;
  value: number;
  icon?: typeof ShieldAlert;
  tone?: "default" | "success" | "warning" | "danger";
}) {
  const toneClass = {
    default: "text-on-surface",
    success: "text-success",
    warning: "text-warning",
    danger: "text-danger",
  }[tone];
  return (
    <div className="flex items-center justify-between rounded-lg border border-border bg-surface-elevated p-3">
      <div className="flex flex-col gap-0.5">
        <span className="text-xs uppercase tracking-wide text-on-surface-muted">
          {label}
        </span>
        <span className={cn("text-2xl font-semibold tracking-tight", toneClass)}>
          {value}
        </span>
      </div>
      {Icon && <Icon className={cn("size-5", toneClass)} />}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Rules tab — real CRUD editor (B0.45). Reads from useAlarms(), writes
// via the store. Phase 1 swaps to /v1/iot/alarms/rules CRUD (already
// shipped server-side — see api/openapi.yaml).
// ────────────────────────────────────────────────────────────────────

const OPS: Array<{ value: AlarmRule["condition"]["op"]; label: string }> = [
  { value: "gt", label: "greater than" },
  { value: "lt", label: "less than" },
  { value: "gte", label: "≥" },
  { value: "lte", label: "≤" },
  { value: "eq", label: "equals" },
  { value: "ne", label: "not equals" },
];

const SEVERITIES: Array<{ value: AlarmRule["severity"]; label: string; variant: "info" | "warning" | "danger" }> = [
  { value: "info", label: "info", variant: "info" },
  { value: "warning", label: "warning", variant: "warning" },
  { value: "critical", label: "critical", variant: "danger" },
];

const CHANNELS = ["email", "in-app", "sms", "slack", "webhook"];

function RulesTab({ prefill }: { prefill?: Partial<DraftRule> | null }) {
  const { rules, toggleRule, deleteRule } = useAlarms();
  const { can } = useOrg();
  // Rule authoring (create/edit/delete) is gated on alarms.write — the
  // permission the backend's rule-CRUD endpoints enforce. The alarm-event ACK
  // action stays viewer-available (it's ungated below — any data.read user).
  const canWrite = can("alarms.write");
  const toast = useToast();
  // A rule's threshold is a number in its tag's unit: read and typed in the org's shown unit.
  const prefs = useShownPrefs();
  const unitOf = useTagUnitOf();
  // A correlation hand-off (prefill) auto-opens the new-rule editor — only for a
  // writer (a viewer's editor would 403 on save).
  const [editing, setEditing] = useState<AlarmRule | "new" | null>(
    prefill && canWrite ? "new" : null,
  );
  const [confirmDelete, setConfirmDelete] = useState<AlarmRule | null>(null);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-semibold text-on-surface">Rules</h2>
          <p className="text-xs text-on-surface-muted">
            Rules fire alarm events when their condition is met. Every rule
            watches one tag: a threshold on its latest reading, or a control-chart signal.
          </p>
        </div>
        {canWrite && (
          <Button size="sm" data-tour="alarm-new-rule" onClick={() => setEditing("new")}>
            <Plus className="mr-1.5 size-4" />
            New rule
          </Button>
        )}
      </div>

      {rules.length === 0 ? (
        <EmptyState
          icon={ShieldAlert}
          title="No rules yet"
          description="Create the first one — alert when a tag crosses a threshold."
          cta={
            canWrite ? (
              <Button size="sm" onClick={() => setEditing("new")}>
                <Plus className="mr-1.5 size-4" />
                New rule
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {rules.map((rule) => {
            const sev = SEVERITIES.find((s) => s.value === rule.severity)!;
            return (
              <Card key={rule.id} className={cn(!rule.enabled && "opacity-60")}>
                <CardHeader className="gap-2">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-sm">{rule.name}</CardTitle>
                    <div className="flex items-center gap-1">
                      <Badge variant={sev.variant}>{sev.label}</Badge>
                      <button
                        type="button"
                        onClick={() => {
                          toggleRule(rule.id);
                          toast.push(
                            "info",
                            `${rule.name} ${rule.enabled ? "disabled" : "enabled"}`,
                          );
                        }}
                        className={cn(
                          "rounded-full px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide",
                          rule.enabled
                            ? "bg-success/15 text-success"
                            : "bg-surface-muted text-on-surface-muted",
                        )}
                        title={rule.enabled ? "Click to disable" : "Click to enable"}
                      >
                        {rule.enabled ? "on" : "off"}
                      </button>
                    </div>
                  </div>
                  <CardDescription className="font-mono text-[11px]">
                    {rule.targetLabel ?? rule.targetTagId}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col gap-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-on-surface-muted">Condition</span>
                    <span className="font-mono text-on-surface">
                      {conditionToString(rule.condition, unitOf(rule.targetTagId), prefs)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-on-surface-muted">Channels</span>
                    <div className="flex flex-wrap gap-1">
                      {rule.channels.map((c) => (
                        <Badge key={c} variant="secondary" className="font-mono">
                          {c}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-on-surface-muted">Last 7 days</span>
                    <span className="font-mono text-on-surface">
                      {rule.triggers7d} fire{rule.triggers7d === 1 ? "" : "s"}
                    </span>
                  </div>
                  {canWrite && (
                    <div className="flex items-center justify-end gap-1 border-t border-border pt-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setConfirmDelete(rule)}
                        title="Delete"
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditing(rule)}>
                        <Edit3 className="mr-1 size-3.5" />
                        Edit
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {editing && (
        <RuleEditor
          // Opens afresh when the tags arrive and the threshold's unit becomes known.
          key={`${editing === "new" ? "new" : editing.id}|${shownFor(unitOf(editing === "new" ? (prefill?.targetTagId ?? "") : editing.targetTagId), prefs).unit}`}
          initial={editing === "new" ? null : editing}
          prefill={editing === "new" ? prefill ?? undefined : undefined}
          unitOf={unitOf}
          onClose={() => setEditing(null)}
        />
      )}

      {confirmDelete && (
        <DeleteConfirm
          rule={confirmDelete}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => {
            deleteRule(confirmDelete.id);
            toast.push("ok", `Deleted "${confirmDelete.name}"`);
            setConfirmDelete(null);
          }}
        />
      )}
    </div>
  );
}

function conditionToString(c: AlarmRule["condition"], storedUnit: string, prefs: DisplayPrefs | undefined): string {
  if (c.op === "spc") return spcAlarmConditionText(c.rules);
  const opStr = OPS.find((o) => o.value === c.op)?.label ?? c.op;
  return thresholdConditionText(opStr, c, storedUnit, prefs);
}

// ────────────────────────────────────────────────────────────────────
// RuleEditor — Sheet dialog for create + edit
// ────────────────────────────────────────────────────────────────────

interface DraftRule {
  name: string;
  targetTagId: string;
  targetLabel: string;
  op: AlarmRule["condition"]["op"];
  value: string;
  forSeconds: string;
  /** S15: a threshold on the latest reading, or a control chart signal (op "spc"). */
  kind: "threshold" | "spc";
  /** S15: the target tag's control chart and the rules that fire the alarm. */
  chartId: string;
  spcRules: string[];
  severity: AlarmRule["severity"];
  channels: string[];
  enabled: boolean;
}

function emptyDraft(): DraftRule {
  return {
    name: "",
    targetTagId: FX_CATALOG_TAGS[0]?.id ?? "",
    targetLabel: FX_CATALOG_TAGS[0]?.displayName ?? "",
    op: "gt",
    value: "",
    forSeconds: "",
    kind: "threshold",
    chartId: "",
    spcRules: [],
    severity: "warning",
    channels: ["in-app"],
    enabled: true,
  };
}

function ruleToDraft(rule: AlarmRule): DraftRule {
  const spc = rule.condition.op === "spc";
  return {
    name: rule.name,
    targetTagId: rule.targetTagId,
    targetLabel: rule.targetLabel ?? rule.targetTagId,
    op: spc ? "gt" : rule.condition.op,
    value: spc ? "" : String(rule.condition.value),
    forSeconds: rule.condition.forSeconds ? String(rule.condition.forSeconds) : "",
    kind: spc ? "spc" : "threshold",
    chartId: spc ? (rule.condition.chartId ?? "") : "",
    // No rules listed means any rule: every one ticked.
    spcRules: spc
      ? rule.condition.rules?.length
        ? rule.condition.rules
        : SPC_ALARM_RULES.map((r) => r.rule)
      : [],
    severity: rule.severity,
    channels: rule.channels,
    enabled: rule.enabled,
  };
}

/** `thing · tag (unit)` label for a live tag option. */
function tagOptionLabel(t: TagOption): string {
  const base = t.thing_name ? `${t.thing_name} · ${t.name}` : t.name;
  return t.unit ? `${base} (${t.unit})` : base;
}

/**
 * Target-tag picker. Live mode: a searchable combobox over the org's real
 * tags (so a tag created moments ago on the Things page appears here
 * immediately). Stub mode: the fixture catalogue select, for local UI dev.
 */
function TargetTagField({
  draft,
  setDraft,
}: {
  draft: DraftRule;
  setDraft: (d: DraftRule) => void;
}) {
  const orgTags = useQuery({
    queryKey: ["org-tags"],
    queryFn: listOrgTags,
    enabled: isLiveApi,
    staleTime: 30_000,
  });

  if (!isLiveApi) {
    return (
      <select
        value={draft.targetTagId}
        onChange={(e) => {
          const tag = FX_CATALOG_TAGS.find((t) => t.id === e.target.value);
          setDraft({
            ...draft,
            targetTagId: e.target.value,
            targetLabel: tag?.displayName ?? e.target.value,
          });
        }}
        className="h-9 w-full rounded-md border border-border bg-surface px-2 text-sm text-on-surface"
      >
        {FX_CATALOG_TAGS.map((t) => (
          <option key={t.id} value={t.id}>
            {t.displayName} {t.unit ? `(${t.unit})` : ""}
          </option>
        ))}
      </select>
    );
  }

  const options = orgTags.data?.items ?? [];
  const selected = options.find((t) => t.id === draft.targetTagId);
  const selectedLabel = selected
    ? tagOptionLabel(selected)
    : draft.targetTagId
      ? draft.targetLabel || draft.targetTagId
      : null;

  return (
    <div className="flex flex-col gap-2">
      {selectedLabel && (
        <div className="flex items-center justify-between rounded-md border border-border bg-surface-muted px-2.5 py-1.5">
          <span className="font-mono text-xs text-on-surface">{selectedLabel}</span>
          <button
            type="button"
            onClick={() => setDraft({ ...draft, targetTagId: "", targetLabel: "" })}
            className="text-[11px] font-medium text-brand hover:underline"
          >
            Change
          </button>
        </div>
      )}
      {!draft.targetTagId && (
        <TagSearchCombobox
          tags={options}
          onSelect={(tagId) => {
            const tag = options.find((t) => t.id === tagId);
            setDraft({
              ...draft,
              targetTagId: tagId,
              targetLabel: tag ? tagOptionLabel(tag) : tagId,
            });
          }}
          placeholder="Search tags by Thing or name…"
          emptyLabel={
            orgTags.isLoading
              ? "Loading tags…"
              : "No tags yet — add a Thing with tags first"
          }
        />
      )}
    </div>
  );
}

function RuleEditor({
  initial,
  prefill,
  unitOf,
  onClose,
}: {
  initial: AlarmRule | null;
  prefill?: Partial<DraftRule>;
  /** The unit each tag stores its readings in: the unit a threshold is held in. */
  unitOf: (tagId: string) => string;
  onClose: () => void;
}) {
  const { createRule, updateRule } = useAlarms();
  const toast = useToast();
  const prefs = useShownPrefs();
  // What the threshold field opened with, as stored and as shown: a threshold left as it opened is saved exactly as it
  // was stored (never a round trip through the shown number).
  const [opened] = useState<OpenedThreshold | undefined>(() => {
    const tagId = initial?.targetTagId ?? prefill?.targetTagId ?? "";
    const stored = initial
      ? initial.condition.op === "spc"
        ? Number.NaN
        : Number(initial.condition.value)
      : prefill?.value !== undefined && prefill.value !== ""
        ? Number(prefill.value)
        : Number.NaN;
    return Number.isFinite(stored) ? { value: stored, text: thresholdFormText(stored, unitOf(tagId), prefs) } : undefined;
  });
  // The field shows the threshold in another unit only where the org's choice converts the tag's: otherwise it is the
  // number as it always was.
  const converts = shownFor(unitOf(initial?.targetTagId ?? prefill?.targetTagId ?? ""), prefs).converted;
  const [draft, setDraft] = useState<DraftRule>(() => {
    if (initial) {
      const d = ruleToDraft(initial);
      return opened && converts && initial.condition.op !== "spc" ? { ...d, value: opened.text } : d;
    }
    const d = emptyDraft();
    // Live mode: start with no target so the searchable picker forces a real
    // tag choice. The fixture default only makes sense in stub mode.
    if (isLiveApi) {
      d.targetTagId = "";
      d.targetLabel = "";
    }
    // Cross-route hand-off (e.g. the correlation "create alarm" action): name /
    // operator / threshold / target label come pre-populated; the user only
    // picks the tag and saves.
    // A suggested threshold is held in the tag's unit; the field shows it in the shown one.
    if (prefill) return { ...d, ...prefill, ...(opened && converts ? { value: opened.text } : {}) };
    return d;
  });

  // The threshold is a number in the target tag's unit; the field reads it in the shown one.
  const storedUnit = unitOf(draft.targetTagId);
  // What the field opened with, as the current tag shows it: a tag picked after a suggestion moves its text with it.
  const openedNow = opened ? { value: opened.value, text: thresholdFormText(opened.value, storedUnit, prefs) } : undefined;
  const valueNum = Number(draft.value);
  // A statistical alarm saves only the ticked rules its chart raises (#926 L3): the server refuses any other.
  const spc = draft.kind === "spc";
  const offered = useSpcAlarmOfferedRules(spc ? draft.targetTagId : "", spc ? draft.chartId : "");
  const spcRules = spcAlarmKeepOffered(draft.spcRules, offered);
  const valid =
    draft.name.trim().length > 1 &&
    draft.targetTagId &&
    (spc
      ? spcAlarmDraftValid(draft.chartId, spcRules)
      : !Number.isNaN(valueNum));

  const handleSave = () => {
    if (!valid) return;
    const condition = draftCondition(draft, spcRules, { storedUnit, prefs, opened: openedNow });
    if (initial) {
      updateRule(initial.id, {
        name: draft.name.trim(),
        targetTagId: draft.targetTagId,
        targetLabel: draft.targetLabel,
        condition,
        severity: draft.severity,
        channels: draft.channels,
        enabled: draft.enabled,
      });
      toast.push("ok", `Updated "${draft.name.trim()}"`);
    } else {
      createRule({
        name: draft.name.trim(),
        targetTagId: draft.targetTagId,
        targetLabel: draft.targetLabel,
        condition,
        severity: draft.severity,
        channels: draft.channels,
        enabled: draft.enabled,
      });
      toast.push("ok", `Created rule "${draft.name.trim()}"`);
    }
    onClose();
  };

  return (
    <Sheet open onClose={onClose} widthClass="max-w-lg" ariaLabel={initial ? "Edit rule" : "New rule"}>
      <SheetHeader
        title={initial ? "Edit rule" : "New rule"}
        subtitle="Fires an alarm event when the condition is true."
        onClose={onClose}
      />

      <div className="flex flex-1 flex-col gap-4 p-5">
        <RuleField label="Name">
          <Input
            autoFocus
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder="Bath temperature high"
          />
        </RuleField>

        <RuleField label="Target tag">
          <div data-tour="alarm-rule-target">
            {initial ? (
              // A saved rule keeps its tag (the server cannot re-target a rule, and a statistical
              // alarm's chart is of that tag): a rule for another tag is a new rule.
              <span className="flex flex-col gap-1">
                <span className="rounded-md border border-border bg-surface-muted px-2.5 py-1.5 font-mono text-xs text-on-surface">
                  {draft.targetLabel || draft.targetTagId}
                </span>
                <span className="text-[11px] text-on-surface-muted">
                  A rule keeps its tag. For another tag, create a new rule.
                </span>
              </span>
            ) : (
              <TargetTagField
                draft={draft}
                setDraft={(d) =>
                  // A statistical alarm watches a chart of its target tag: a new tag drops the chart. A threshold left
                  // as it opened reads in the new tag's unit (#1190 review MEDIUM 3).
                  setDraft(
                    d.targetTagId !== draft.targetTagId
                      ? {
                          ...d,
                          chartId: "",
                          spcRules: [],
                          value: thresholdRetargeted({
                            text: d.value,
                            opened: opened?.value,
                            fromUnit: unitOf(draft.targetTagId),
                            toUnit: unitOf(d.targetTagId),
                            prefs,
                          }),
                        }
                      : d,
                  )
                }
              />
            )}
          </div>
        </RuleField>

        <ConditionKindToggle kind={draft.kind} onChange={(kind) => setDraft({ ...draft, kind })} />

        {draft.kind === "spc" ? (
          <SpcAlarmConditionFields
            tagId={draft.targetTagId}
            chartId={draft.chartId}
            rules={draft.spcRules}
            onChange={({ chartId, rules }) => setDraft({ ...draft, chartId, spcRules: rules })}
          />
        ) : (
        <div className="grid grid-cols-3 gap-2" data-tour="alarm-rule-condition">
          <RuleField label="Operator">
            <select
              value={draft.op}
              onChange={(e) =>
                setDraft({ ...draft, op: e.target.value as AlarmRule["condition"]["op"] })
              }
              className="h-9 w-full rounded-md border border-border bg-surface px-2 text-sm text-on-surface"
            >
              {OPS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </RuleField>
          <RuleField label={thresholdLabel(storedUnit, prefs)}>
            <Input
              value={draft.value}
              onChange={(e) => setDraft({ ...draft, value: e.target.value })}
              placeholder="1500"
            />
          </RuleField>
          <RuleField label="For (sec)">
            <Input
              value={draft.forSeconds}
              onChange={(e) => setDraft({ ...draft, forSeconds: e.target.value })}
              placeholder="30"
            />
          </RuleField>
        </div>
        )}

        <RuleField label="Severity">
          <div className="flex gap-1.5">
            {SEVERITIES.map((s) => {
              const active = draft.severity === s.value;
              return (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => setDraft({ ...draft, severity: s.value })}
                  className={cn(
                    "flex-1 rounded-md border px-2 py-1.5 text-xs font-medium capitalize",
                    active
                      ? "border-brand bg-brand text-brand-foreground"
                      : "border-border bg-surface text-on-surface hover:border-brand/60",
                  )}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </RuleField>

        <RuleField label="Channels">
          <div className="flex flex-wrap gap-1.5">
            {CHANNELS.map((c) => {
              const active = draft.channels.includes(c);
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() =>
                    setDraft({
                      ...draft,
                      channels: active
                        ? draft.channels.filter((x) => x !== c)
                        : [...draft.channels, c],
                    })
                  }
                  className={cn(
                    "rounded-full border px-2.5 py-0.5 font-mono text-[11px] transition-colors",
                    active
                      ? "border-brand bg-brand text-brand-foreground"
                      : "border-border bg-surface text-on-surface-muted hover:border-on-surface",
                  )}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </RuleField>

        <label className="flex cursor-pointer items-center gap-2 text-xs text-on-surface">
          <input
            type="checkbox"
            checked={draft.enabled}
            onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
            className="size-3.5"
          />
          Enabled — rule actively evaluates incoming readings
        </label>

        <div className="mt-auto flex items-center justify-between border-t border-border pt-4">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" data-tour="alarm-rule-save" onClick={handleSave} disabled={!valid}>
            {initial ? "Save changes" : "Create rule"}
          </Button>
        </div>
      </div>
    </Sheet>
  );
}

/**
 * The condition a draft saves: a control chart signal (S15, op "spc": the chart and the ticked rules it raises,
 * in the list's order) or a threshold on the latest reading.
 */
function draftCondition(
  draft: DraftRule,
  spcRules: readonly string[],
  threshold: { storedUnit: string; prefs: DisplayPrefs | undefined; opened?: OpenedThreshold },
): AlarmRule["condition"] {
  if (draft.kind === "spc") {
    const body = buildSpcAlarmCondition(draft.chartId, spcRules);
    return { op: "spc", value: "", chartId: body.chart_id, ...(body.rules ? { rules: body.rules } : {}) };
  }
  return {
    op: draft.op,
    // Typed in the shown unit, saved in the tag's; an empty field is 0, as it always was.
    value: thresholdSaved({ text: draft.value, ...threshold }) ?? Number(draft.value),
    ...(draft.forSeconds ? { forSeconds: Number(draft.forSeconds) || undefined } : {}),
  };
}

/** Threshold or control chart signal: which condition the rule has. */
function ConditionKindToggle({
  kind,
  onChange,
}: {
  kind: DraftRule["kind"];
  onChange: (kind: DraftRule["kind"]) => void;
}) {
  const kinds: Array<{ value: DraftRule["kind"]; label: string }> = [
    { value: "threshold", label: "Threshold" },
    { value: "spc", label: "Control chart signal" },
  ];
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">Condition</span>
      <div className="flex gap-1.5" role="group" aria-label="Condition">
        {kinds.map((k) => (
          <button
            key={k.value}
            type="button"
            aria-pressed={kind === k.value}
            onClick={() => onChange(k.value)}
            className={cn(
              "flex-1 rounded-md border px-2 py-1.5 text-xs font-medium",
              kind === k.value
                ? "border-brand bg-brand text-brand-foreground"
                : "border-border bg-surface text-on-surface hover:border-brand/60",
            )}
          >
            {k.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function RuleField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
        {label}
      </span>
      {children}
    </label>
  );
}

function DeleteConfirm({
  rule,
  onCancel,
  onConfirm,
}: {
  rule: AlarmRule;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Sheet open onClose={onCancel} widthClass="max-w-md" ariaLabel="Confirm delete">
      <SheetHeader title="Delete rule" subtitle="This cannot be undone." onClose={onCancel} />
      <div className="flex flex-1 flex-col gap-4 p-5">
        <p className="text-sm text-on-surface">
          Permanently delete the rule <strong>{rule.name}</strong>? It targets{" "}
          <code className="font-mono">{rule.targetLabel ?? rule.targetTagId}</code> and has fired{" "}
          {rule.triggers7d} time{rule.triggers7d === 1 ? "" : "s"} in the last week.
        </p>
        <div className="mt-auto flex items-center justify-end gap-2 border-t border-border pt-4">
          <Button size="sm" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="sm" variant="danger" onClick={onConfirm}>
            <Trash2 className="mr-1.5 size-3.5" />
            Delete rule
          </Button>
        </div>
      </div>
    </Sheet>
  );
}
